import { TOOL_NAMES } from 'js/src/agent/toolNames';

/**
 * The timing guidance shared by the how-to skills, so voiceover, talking-head, and text videos
 * describe when an asset should appear in one voice. The narration variant is used verbatim by the
 * voiceover and talking-head skills: lint_video times their text, so it covers overriding that. The
 * text-video skill has no transcript and keeps its own milliseconds framing, sharing only the group
 * and icon rules below.
 */

/** How grouped children and paired icons are timed — the rules the tools enforce, for any vocabulary. */
export function groupedAssetTimingRules(param: 'start_words' | 'start_time_ms'): string {
    return `Grouped text (a \`GROUP\` with \`children\` in the video state — a list, or a heading with its
subtitle) follows two rules the tools enforce:
- Children reveal in order, top to bottom, and no child appears before its group. Time the
  children one at a time with \`${TOOL_NAMES.setText}\`, earlier items first; a child timed before
  an earlier sibling, or before its group, is rejected.
- Time the group itself with \`${TOOL_NAMES.setGroupAsset}\` only when the whole thing should land
  at once — it moves the group and every child together. Do that first if the group's own
  timing needs to move, then stagger the children after.

An icon that illustrates a text asset lands with it: give \`${TOOL_NAMES.setIcon}\` the same
\`${param}\` you gave the text. An icon showing from the first frame while its label waits reads
as a mistake.

The video state reports an asset's current timing as \`appearsAt\`. No \`appearsAt\` means the
asset is visible from the scene's first frame.`;
}

/** Timing text to spoken narration — voiceover and talking-head scenes alike. */
export function narrationTimingGuidance(): string {
    return `## Timing text to the narration
Don't time text and icons to the narration yourself — write them untimed. When you run
\`${TOOL_NAMES.lintVideo}\`, every text and icon still on its design's default timing is landed on the
narration word it introduces (a list item on the word that names it, a stat as the number is said),
and the result lists what moved. Leave those as they are unless one is clearly wrong for the video.

To move one, pass \`start_words\` to \`${TOOL_NAMES.setText}\` or \`${TOOL_NAMES.setIcon}\`: consecutive
transcript words copied verbatim, in the same notation \`${TOOL_NAMES.addVoiceoverScene}\` takes (pause
tags included), falling inside the scene's own \`covers\` range. Lint only times assets nobody has
timed, so timing you set stays put.

${groupedAssetTimingRules('start_words')}`;
}
